import React, { createContext, useContext, useState, useEffect } from 'react';
import { getValidToken } from '../utils/auth';
import { clearPersistedView, persistView, readPersistedView } from '../utils/viewStorage';
import {
  getAllowedViewsForAuthPayload,
  getDefaultViewForAuthPayload,
} from '../utils/roleCapabilities';

// Create a Context for the view
const ViewContext = createContext();
const normalizeLegacyView = (view) => (view === 'recruiter' ? 'professional' : view);

// Create a provider component
export const ViewProvider = ({ children }) => {
  const [currentView, setCurrentView] = useState(() => {
    const savedView = normalizeLegacyView(readPersistedView());
    const auth = getValidToken();
    const authPayload = auth?.payload;

    if (savedView && (!authPayload?.role || getAllowedViewsForAuthPayload(authPayload).includes(savedView))) {
      return savedView;
    }

    if (authPayload?.role) {
      return getDefaultViewForAuthPayload(authPayload);
    }

    return null;
  });

  // ✅ Save view to localStorage whenever it changes
  useEffect(() => {
    if (currentView === null) {
      clearPersistedView();
      return;
    }

    persistView(currentView);
  }, [currentView]);

  useEffect(() => {
    const auth = getValidToken();
    const authPayload = auth?.payload;

    if (!authPayload?.role) {
      setCurrentView('customer');
      return;
    }

    setCurrentView((previousView) => {
      const normalizedPreviousView = normalizeLegacyView(previousView);
      if (normalizedPreviousView !== previousView) {
        return normalizedPreviousView;
      }

      if (getAllowedViewsForAuthPayload(authPayload).includes(normalizedPreviousView)) {
        return normalizedPreviousView;
      }

      return getDefaultViewForAuthPayload(authPayload);
    });
  }, [setCurrentView]);

  return (
    <ViewContext.Provider value={{ currentView, setCurrentView }}>
      {children}
    </ViewContext.Provider>
  );
};

// Create a custom hook to use the ViewContext
export const useView = () => useContext(ViewContext);
